import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Lock, Upload, Languages, Info } from "lucide-react";
import { GalleryField, RelatedField } from "@/components/admin/MediaFields";

export const CATEGORIES = ["Rally & Gare", "Interviste", "Tecnica & Tuning", "Legend Team", "Motorsport"];
export const SLOTS = ["masthead-left", "masthead-right", "banner", "inline"];

export function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try { await login(email, password); }
    catch (e) { setErr(e.response?.data?.detail || "Credenziali non valide"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-sm border-2 border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper p-8">
        <div className="flex items-center gap-2 mb-6"><Lock size={22} className="text-legend-red" /><h1 className="font-display font-black uppercase text-2xl">Area Redazione</h1></div>
        {err && <p className="mb-4 text-sm text-legend-red bg-legend-red/10 px-3 py-2 rounded-sm">{err}</p>}
        <div className="mb-5 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-slate-800 rounded-sm p-3" data-testid="admin-login-info">
          <Info size={16} className="text-legend-red shrink-0 mt-0.5" />
          <p>L'<strong>Area Redazione</strong> è l'accesso riservato alla redazione degli uffici amministrativi di <strong>Legend Magazine</strong> e ai suoi collaboratori, inclusi i giornalisti. Da qui si gestiscono notizie, articoli, gare e pubblicità.</p>
        </div>
        <div className="space-y-4">
          <div><Label>Email</Label><Input type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div><Label>Password</Label><Input type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full inline-flex items-center justify-center gap-2 bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-5 py-3 rounded-sm transition-colors disabled:opacity-60">
            {loading && <Loader2 size={16} className="animate-spin" />} Accedi
          </button>
        </div>
      </form>
    </div>
  );
}

export function Field({ label, textarea, ...props }) {
  return <div><Label>{label}</Label>{textarea ? <Textarea {...props} /> : <Input {...props} />}</div>;
}

export function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const src = value ? (value.startsWith("http") ? value : `${backend}${value}`) : "";
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Immagine caricata!");
    } catch { toast.error("Upload fallito."); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        {src ? <img src={src} alt="" className="w-20 h-14 object-cover rounded-sm border border-slate-200 dark:border-slate-700" /> : <div className="w-20 h-14 grid place-items-center bg-slate-100 dark:bg-white/5 rounded-sm text-[10px] text-slate-400">nessuna</div>}
        <label className="inline-flex items-center gap-2 cursor-pointer bg-legend-ink text-white text-sm font-bold uppercase px-3 py-2 rounded-sm hover:bg-slate-800">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Carica
          <input type="file" accept="image/*" className="hidden" data-testid={testid} onChange={upload} />
        </label>
      </div>
      <Input className="mt-2" placeholder="oppure incolla un URL" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password aggiornata!");
      setOpen(false); setCur(""); setNw("");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-change-password" className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-4 py-2 rounded-sm text-sm font-semibold hover:bg-legend-ink hover:text-white transition-colors"><Lock size={15} /> Cambia password</button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase text-xl">Cambia password</DialogTitle><DialogDescription>Imposta una password personale (min. 8 caratteri).</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Password attuale</Label><Input type="password" data-testid="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} required /></div>
          <div><Label>Nuova password</Label><Input type="password" data-testid="cp-new" value={nw} onChange={(e) => setNw(e.target.value)} required minLength={8} /></div>
          <button type="submit" disabled={saving} data-testid="cp-submit" className="w-full inline-flex items-center justify-center gap-2 bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-5 py-3 rounded-sm disabled:opacity-60">{saving && <Loader2 size={16} className="animate-spin" />} Aggiorna</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TranslateButton({ form, set }) {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await client.post("/admin/translate", {
        title: form.title || "", subtitle: form.subtitle || "",
        excerpt: form.excerpt || "", body: form.body || "",
      });
      set("title_en", data.title_en || "");
      set("subtitle_en", data.subtitle_en || "");
      set("excerpt_en", data.excerpt_en || "");
      set("body_en", data.body_en || "");
      toast.success("Tradotto in inglese con l'IA!");
    } catch { toast.error("Traduzione non riuscita."); }
    finally { setLoading(false); }
  };
  return (
    <button type="button" onClick={run} disabled={loading} data-testid="ai-translate-btn"
      className="w-full inline-flex items-center justify-center gap-2 border-2 border-legend-red text-legend-red font-bold uppercase tracking-wide px-4 py-2.5 rounded-sm hover:bg-legend-red hover:text-white transition-colors disabled:opacity-60">
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />} Traduci in inglese (AI)
    </button>
  );
}

export function EntityDialog({ title, fields, initial, onSave, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial); }, [open]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); setOpen(false); toast.success("Salvato!"); }
    catch { toast.error("Errore durante il salvataggio."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="entity-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase text-xl">{title}</DialogTitle><DialogDescription>Compila i campi e salva le modifiche.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {fields.map((f) => {
            if (f.type === "select") return (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <select value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} className="w-full rounded-sm border border-slate-300 dark:border-slate-700 bg-white dark:bg-legend-paper px-3 py-2 text-sm">
                  {f.allowEmpty && <option value="">— nessuno —</option>}
                  {form[f.key] && !f.options.some((o) => (o.value ?? o) === form[f.key]) && <option value={form[f.key]}>{form[f.key]}</option>}
                  {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
                </select>
              </div>
            );
            if (f.type === "checkbox") return (
              <label key={f.key} className="flex items-center gap-2 py-1"><input type="checkbox" checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} data-testid={`field-${f.key}`} className="w-5 h-5 accent-legend-red" /> <span className="text-sm font-semibold">{f.label}</span></label>
            );
            if (f.type === "image") return (
              <ImageField key={f.key} label={f.label} value={form[f.key]} onChange={(v) => set(f.key, v)} testid={`upload-${f.key}`} />
            );
            if (f.type === "ai-translate") return <TranslateButton key={f.key} form={form} set={set} />;
            if (f.type === "gallery") return <GalleryField key={f.key} label={f.label} value={form[f.key] || []} onChange={(v) => set(f.key, v)} />;
            if (f.type === "related") return <RelatedField key={f.key} label={f.label} options={f.options.filter((o) => o.value !== form.slug)} value={form[f.key] || []} onChange={(v) => set(f.key, v)} />;
            if (f.type === "section") return <p key={f.key} className="pt-3 mt-2 border-t border-slate-200 dark:border-slate-800 font-display font-bold uppercase text-xs tracking-widest text-legend-red">{f.label}</p>;
            return <Field key={f.key} label={f.label} textarea={f.type === "textarea"} rows={f.type === "textarea" ? (f.rows || 4) : undefined} type={f.type === "number" ? "number" : "text"} step={f.type === "number" ? "any" : undefined} data-testid={`field-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => set(f.key, f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} placeholder={f.placeholder} required={f.required} />;
          })}
          <DialogFooter>
            <button type="submit" disabled={saving} data-testid="entity-save" className="w-full inline-flex items-center justify-center gap-2 bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-5 py-3 rounded-sm disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Salva
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const AddBtn = (props) => <button {...props} className="inline-flex items-center gap-1.5 bg-legend-red hover:bg-legend-dark text-white text-sm font-bold uppercase px-4 py-2 rounded-sm"><Plus size={15} /> Aggiungi</button>;
export const IconBtn = ({ children, ...p }) => <button {...p} className="grid place-items-center w-9 h-9 rounded-sm border border-slate-300 dark:border-slate-700 hover:bg-legend-red hover:text-white hover:border-legend-red transition-colors">{children}</button>;
